import { useTranslation } from "react-i18next";
import useAuth from "../../../../hooks/useAuth";
import { Link, useParams } from "react-router-dom";
import { CustomButton } from "../../../atoms/CustomButton";
import { EmptyState } from "../../../atoms/EmptyState";
import { Spinner } from "../../../atoms/Spinner";
import {
  HiArrowLeft,
  HiOutlineDocumentText,
  HiOutlinePencilAlt,
  HiOutlineTrash,
} from "react-icons/hi";
import { useGetUserData, useUpdateKeyMutation } from "../../../../hooks/users";
import { useCallback, useState } from "react";
import AddNoteModal from "../../../modals/AddNoteModal";
import moment from "moment";
import ConfirmModal from "../../../public/ConfirmModal";
import { queryClient } from "../../../../providers/QueryProvider";
import { USER_NOTES } from "../../../../constants/types";
import NoTranslate from "../../../atoms/NoTranslate";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

const UserNotes = ({
  isRoute = false,
  hideHeading = false,
}: {
  isRoute?: boolean;
  hideHeading?: boolean;
}) => {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState<{
    type: "edit" | "delete" | null;
    data: any;
  }>({
    type: null,
    data: null,
  });
  const isEditOpen = modalState.type === "edit";
  const isDeleteOpen = modalState.type === "delete";

  const { customer_identification, user } = useAuth();
  const permissions = usePermissions(user);
  const canWriteNotes = permissions.can(Cap.USER_NOTES, Level.WRITE);
  const canDeleteNotes = permissions.can(Cap.USER_NOTES, Level.DELETE);
  const { user_identification } = useParams();

  const { data, isLoading, isError, refetch, isSuccess } = useGetUserData(
    customer_identification,
    user.entity_identification,
    user_identification as string,
    USER_NOTES
  );

  const { mutate: updateKey, isLoading: isUpdating } = useUpdateKeyMutation();

  const deleteNote = () => {
    updateKey(
      {
        customer_identification: customer_identification,
        entityIdentification: user.entity_identification,
        customerId: user_identification,
        key: USER_NOTES,
        value: [
          ...data?.user_data[USER_NOTES]?.filter(
            (note: { key: Date }) => note.key !== modalState.data
          ),
        ],
      },
      {
        onSuccess: () => {
          setModalState({ type: null, data: null });
          queryClient.invalidateQueries(["getUserData", user_identification]);
        },
      }
    );
  };
  const renderList = useCallback(() => {
    const notes = data?.user_data[USER_NOTES];
    if (!Array.isArray(notes) || notes.length === 0) {
      return (
        <EmptyState
          className="mt-6"
          icon={<HiOutlineDocumentText size={24} />}
          title={t("dashboard.entities.viewEntityUser.key21")}
        />
      );
    }

    const sortedNotes = [...notes].sort(
      (a, b) => new Date(b.key).getTime() - new Date(a.key).getTime()
    );

    return (
      <ol className="relative mt-6 ml-3 flex flex-col gap-5 border-l border-gray-200 pl-5">
        {sortedNotes.map((note: { key: Date; value: [{ note: string }] }) => (
          <li
            key={note.key.toString()}
            id={note.key.toString()}
            className="relative"
          >
            <span className="absolute -left-8 flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-blue-500 ring-4 ring-white">
              <HiOutlineDocumentText size={13} />
            </span>

            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-medium text-gray-500">
                {moment.utc(note.key).local().format("lll")}
              </p>
              <div className="flex flex-shrink-0 items-center gap-1">
                {canWriteNotes && (
                  <CustomButton
                    variant="none"
                    size="icon"
                    className="text-gray-400 transition-colors enabled:hover:text-blue-600"
                    icon={<HiOutlinePencilAlt size={18} />}
                    tooltip={t("common.edit") as string}
                    onClick={() => {
                      setModalState({ type: "edit", data: note });
                    }}
                  />
                )}
                {canDeleteNotes && (
                  <CustomButton
                    variant="none"
                    size="icon"
                    className="text-gray-400 transition-colors enabled:hover:text-red-500"
                    icon={<HiOutlineTrash size={18} />}
                    tooltip={t("common.delete") as string}
                    onClick={() => {
                      setModalState({ type: "delete", data: note.key });
                    }}
                  />
                )}
              </div>
            </div>

            <div className="mt-1 rounded-lg bg-gray-50 p-3">
              <p className="whitespace-pre-line break-words text-sm text-gray-700">
                <NoTranslate>{note.value[0].note}</NoTranslate>
              </p>
            </div>
          </li>
        ))}
      </ol>
    );
  }, [data, t, canWriteNotes, canDeleteNotes]);

  let content;

  if (isLoading)
    content = (
      <div className="flex items-center justify-center py-12">
        <Spinner size="md" />
      </div>
    );

  if (isError)
    content = (
      <div className="mt-6 flex flex-col items-center justify-center gap-3 py-12 text-center">
        <p className="text-sm text-gray-500">
          {t("dashboard.entities.viewEntityUser.key23")}
        </p>
        <CustomButton
          variant="secondary"
          onClick={() => refetch()}
          title={t("dashboard.entities.viewEntityUser.key24")}
        />
      </div>
    );

  if (isSuccess) {
    content = renderList();
  }

  return (
    <div className={`p-6`}>
      {isRoute && (
        <Link
          to={"/control-panel/users"}
          className="gap-2 inline-flex items-center mb-4"
        >
          <div className="w-5 h-5 relative">
            <HiArrowLeft size={20} className="text-gray-400" />
          </div>
          <div className="grow shrink basis-0 text-gray-500 text-sm font-semibold font-figtree leading-tight">
            {t("dashboard.entities.viewEntityUser.key1")}
          </div>
        </Link>
      )}

      {!hideHeading && (
        <>
          <h2 className="text-gray-700 text-2xl font-bold font-figtree leading-none mb-2">
            {t("dashboard.entities.viewEntityUser.key17")}
          </h2>
          <p className="text-gray-900 text-base font-normal font-figtree leading-normal">
            {t("dashboard.entities.viewEntityUser.key18")}
          </p>
        </>
      )}

      {canWriteNotes && (
        <CustomButton
          title={t("dashboard.entities.viewEntityUser.key19")}
          icon="plus"
          onClick={() => setModalState({ type: "edit", data: null })}
          variant="primary"
          className="w-full mt-3"
        />
      )}

      {content}
      {isEditOpen && (
        <AddNoteModal
          showModal={true}
          setShowModal={() => setModalState({ type: null, data: null })}
          editData={modalState.data}
        />
      )}

      {isDeleteOpen && (
        <ConfirmModal
          cancelBtnName={t("dashboard.UserDetailsSpace.key3")}
          cancelBtnFn={() => setModalState({ type: null, data: null })}
          onClose={() => setModalState({ type: null, data: null })}
          confirmBtnFn={() => {
            if (!canDeleteNotes) return;
            deleteNote();
          }}
          show={true}
          isLoading={isUpdating}
          ConfirmBtnColor="blue"
          confirmBtnName={t("dashboard.UserDetailsSpace.key1")}
          title={t("dashboard.entities.viewEntityUser.key29")}
          description={t("dashboard.entities.viewEntityUser.key20")}
        />
      )}
    </div>
  );
};

export default UserNotes;
